import React, { useState, useEffect, useCallback } from 'react';
import { onRequestFailure } from '../components/AuthHandler.js';
import '../css/Screenshots.css';

const STORAGE_KEY = 'catapult.runs';
const PROJECT = 'hafgufa';
const CATAPULT_URL = process.env.REACT_APP_CATAPULT_URL;

const authHeaders = () => ({
  'Authorization': `token=${process.env.REACT_APP_CATAPULT_TOKEN}`,
  'Content-Type': 'application/json',
});

async function catapultRequest(url, options) {
  const response = await fetch(url, {
    ...options,
    headers: { ...authHeaders(), ...(options && options.headers) },
  });
  if (response.status === 403 || response.status === 401) {
    onRequestFailure();
    throw new Error(`${response.status} ${response.statusText}`);
  }
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`${response.status} ${response.statusText}${text ? ': ' + text : ''}`);
  }
  return response.json();
}

const startRun = (commit) =>
  catapultRequest(`${CATAPULT_URL}/screenshots/${PROJECT}/run/${encodeURIComponent(commit)}`, {
    method: 'POST',
  });

const fetchRun = (commit, runId) =>
  catapultRequest(
    `${CATAPULT_URL}/screenshots/${PROJECT}/${encodeURIComponent(commit)}/${encodeURIComponent(runId)}`,
  );

const loadRuns = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const persistRuns = (runs) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(runs));
};

const SHA_LENGTH = 7;

const shortSha = (sha) => (sha && sha.length > SHA_LENGTH ? sha.slice(0, SHA_LENGTH) : sha);

const formatTime = (ms) => {
  try {
    return new Date(ms).toLocaleString();
  } catch (e) {
    return String(ms);
  }
};

const STATUS_KIND = {
  JOB_STATE_SUCCEEDED: 'success',
  JOB_STATE_FAILED: 'error',
  JOB_STATE_CANCELLED: 'error',
  JOB_STATE_EXPIRED: 'error',
  ERROR: 'error',
  RESULT_FILE_NOT_FOUND: 'error',
  'Failed to create': 'error',
  NOT_STARTED: 'neutral',
  JOB_STATE_PENDING: 'progress',
  JOB_STATE_RUNNING: 'progress',
  JOB_STATE_QUEUED: 'progress',
};

const statusKind = (status) => STATUS_KIND[status] || 'progress';

const resultKind = (result) => {
  if (!result) return 'neutral';
  const upper = String(result).toUpperCase();
  if (upper === 'PASS' || upper === 'PASSED') return 'success';
  if (upper === 'FAIL' || upper === 'FAILED') return 'error';
  return 'neutral';
};

const summarize = (items) =>
  items.reduce(
    (acc, item) => {
      if (item.kind === 'success') acc.passed += 1;
      else if (item.kind === 'error') acc.failed += 1;
      else acc.other += 1;
      return acc;
    },
    { passed: 0, failed: 0, other: 0 },
  );

// Sort cycles unsorted -> failures first -> successes first.
const SORT_MODES = [null, 'error', 'success'];
const SORT_RANK = { error: 0, neutral: 1, success: 2 };

const sortItems = (items, mode) => {
  if (!mode) return items;
  const sign = mode === 'error' ? 1 : -1;
  return items.slice().sort((a, b) => sign * (SORT_RANK[a.kind] - SORT_RANK[b.kind]));
};

// The screenshots are served from S3 with a generic content type, so a plain
// link navigation makes the browser download them. Loading the URL as an <img>
// in a blank tab renders it instead, since content sniffing applies to
// subresources. Built with DOM calls rather than document.write to avoid
// interpolating a URL into markup.
const openImageInTab = (event, url) => {
  // No 'noopener' here: that makes window.open return null, and we need the
  // handle to write into the new document. The tab only ever holds our own
  // about:blank content, so there is nothing to leak to.
  const tab = window.open('', '_blank');
  if (!tab) return; // popup blocked - fall through to the anchor's own navigation
  event.preventDefault();
  tab.document.title = url.split('/').pop() || 'screenshot';
  tab.document.body.style.margin = '0';
  tab.document.body.style.background = '#1e1e1e';
  const img = tab.document.createElement('img');
  img.src = url;
  img.style.maxWidth = '100%';
  img.style.height = 'auto';
  img.style.display = 'block';
  img.style.margin = '0 auto';
  tab.document.body.appendChild(img);
};

const Screenshots = () => {
  const [runs, setRuns] = useState(loadRuns);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [commitInput, setCommitInput] = useState('');
  const [expanded, setExpanded] = useState({});
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState(null);

  useEffect(() => {
    persistRuns(runs);
  }, [runs]);

  const selectedRun = runs.find((r) => r.runId === selectedRunId) || null;

  const loadRun = useCallback(async (commit, runId) => {
    setLoading(true);
    setError(null);
    setSelectedRunId(runId);
    setResult(null);
    setExpanded({});
    try {
      const data = await fetchRun(commit, runId);
      setResult(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRun = async () => {
    const commit = commitInput.trim();
    if (!commit) return;
    setLoading(true);
    setError(null);
    try {
      const data = await startRun(commit);
      const newRun = { commit: data.commit || commit, runId: data.runId, createdAt: Date.now() };
      setRuns((prev) => [newRun, ...prev]);
      setCommitInput('');
      await loadRun(newRun.commit, newRun.runId);
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  };

  const handleRefresh = (run) => {
    loadRun(run.commit, run.runId);
  };

  const handleRemove = (run) => {
    setRuns((prev) => prev.filter((r) => r.runId !== run.runId));
    if (selectedRunId === run.runId) {
      setSelectedRunId(null);
      setResult(null);
      setExpanded({});
    }
  };

  const panelKey = (rowKey, panel) => `${rowKey}|${panel}`;

  const isOpen = (rowKey, panel) => Boolean(expanded[panelKey(rowKey, panel)]);

  const togglePanel = (rowKey, panel) =>
    setExpanded((prev) => {
      const key = panelKey(rowKey, panel);
      return { ...prev, [key]: !prev[key] };
    });

  const rawItems = (result && Array.isArray(result.results) && result.results) || [];
  const items = rawItems.map((item, index) => ({
    ...item,
    rowKey: item.testCaseId || `row-${index}`,
    kind: resultKind(item.result),
  }));
  const counts = summarize(items);
  const visibleItems = sortItems(
    filter === 'all' ? items : items.filter((item) => item.kind === filter),
    sort,
  );

  const cycleSort = () => setSort((prev) => SORT_MODES[(SORT_MODES.indexOf(prev) + 1) % SORT_MODES.length]);

  const sortLabel = sort === 'error' ? '▲' : sort === 'success' ? '▼' : '↕';
  const sortTitle =
    sort === 'error'
      ? 'Failures first - click for successes first'
      : sort === 'success'
        ? 'Successes first - click to clear sorting'
        : 'Click to sort failures first';

  return (
    <div id="Screenshots" className="screenshots-view">
      <h2>Screenshot Comparisons</h2>

      {!CATAPULT_URL && (
        <div className="screenshots-banner screenshots-banner--warn">
          REACT_APP_CATAPULT_URL is not set. Add it to your .env and restart the dev server.
        </div>
      )}

      <label className="screenshots-label" htmlFor="screenshots-commit">
        Commit SHA
        <span className="screenshots-label-note">
          Short form only — exactly {SHA_LENGTH} characters, e.g. <code>858f528</code>. Copy it from
          the run in Testmo.
        </span>
      </label>

      <div className="screenshots-run-form">
        <input
          id="screenshots-commit"
          type="text"
          className="screenshots-input screenshots-input--sha"
          placeholder="858f528"
          maxLength={SHA_LENGTH}
          value={commitInput}
          onChange={(e) => setCommitInput(e.target.value)}
          disabled={loading}
        />
        <button
          type="button"
          className="screenshots-button screenshots-button--primary"
          onClick={handleRun}
          disabled={loading || !commitInput.trim()}
        >
          {loading && !selectedRun ? 'Running…' : 'Run'}
        </button>
      </div>

      {error && <div className="screenshots-banner screenshots-banner--error">{error}</div>}

      <h3>Saved runs</h3>
      {runs.length === 0 ? (
        <p className="screenshots-empty">No runs yet. Enter a commit sha above to start one.</p>
      ) : (
        <ul className="screenshots-run-list">
          {runs.map((run) => (
            <li
              key={run.runId}
              className={`screenshots-run-row ${selectedRunId === run.runId ? 'is-selected' : ''}`}
            >
              <span className="screenshots-run-commit" title={run.commit}>{shortSha(run.commit)}</span>
              <span className="screenshots-run-id" title={run.runId}>runId={run.runId}</span>
              <span className="screenshots-run-time">{formatTime(run.createdAt)}</span>
              <button
                type="button"
                className="screenshots-button"
                onClick={() => handleRefresh(run)}
                disabled={loading}
              >
                {loading && selectedRunId === run.runId ? 'Refreshing…' : 'Refresh'}
              </button>
              <button
                type="button"
                className="screenshots-button screenshots-button--icon"
                onClick={() => handleRemove(run)}
                title="Remove from list"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      {selectedRun && (
        <div className="screenshots-details">
          <h3>
            Run details: {shortSha(selectedRun.commit)} · {selectedRun.runId}
          </h3>
          {loading && !result && <p>Loading…</p>}
          {result && (
            <>
              <div className="screenshots-status-row">
                <span className={`screenshots-pill screenshots-pill--${statusKind(result.status)}`}>
                  {result.status || 'UNKNOWN'}
                </span>
                {result.error && <span className="screenshots-error-text">{result.error}</span>}
              </div>

              {items.length > 0 && (
                <div className="screenshots-summary">
                  <button
                    type="button"
                    className={`screenshots-count screenshots-count--success ${filter === 'success' ? 'is-active' : ''}`}
                    onClick={() => setFilter(filter === 'success' ? 'all' : 'success')}
                    aria-pressed={filter === 'success'}
                  >
                    {counts.passed} succeeded
                  </button>
                  <button
                    type="button"
                    className={`screenshots-count screenshots-count--error ${filter === 'error' ? 'is-active' : ''}`}
                    onClick={() => setFilter(filter === 'error' ? 'all' : 'error')}
                    aria-pressed={filter === 'error'}
                  >
                    {counts.failed} failed
                  </button>
                  {counts.other > 0 && (
                    <button
                      type="button"
                      className={`screenshots-count screenshots-count--neutral ${filter === 'neutral' ? 'is-active' : ''}`}
                      onClick={() => setFilter(filter === 'neutral' ? 'all' : 'neutral')}
                      aria-pressed={filter === 'neutral'}
                    >
                      {counts.other} unknown
                    </button>
                  )}
                  <span className="screenshots-count-total">
                    of {items.length} test{items.length === 1 ? '' : 's'}
                  </span>
                  {filter !== 'all' && (
                    <button
                      type="button"
                      className="screenshots-button screenshots-button--toggle"
                      onClick={() => setFilter('all')}
                    >
                      Clear filter
                    </button>
                  )}
                </div>
              )}

              {items.length > 0 && (
                <table className="screenshots-table">
                  <thead>
                    <tr>
                      <th className="screenshots-col-shots">Screenshots</th>
                      <th className="screenshots-col-testmo">Testmo</th>
                      <th className="screenshots-col-result">
                        <button
                          type="button"
                          className="screenshots-sort"
                          onClick={cycleSort}
                          title={sortTitle}
                        >
                          Result <span className="screenshots-sort-arrow">{sortLabel}</span>
                        </button>
                      </th>
                      <th className="screenshots-col-details">Details</th>
                    </tr>
                  </thead>
                  {visibleItems.map((item) => {
                    const rowKey = item.rowKey;
                    const deviations = Array.isArray(item.deviations) ? item.deviations : [];
                    const hasAnalysis = Boolean(item.analysis) || deviations.length > 0;
                    const caseUrl = item.testmoUrl || null;
                    const shots = Array.isArray(item.screenshots) ? item.screenshots : [];

                    return (
                      <tbody key={rowKey}>
                        <tr className="screenshots-row">
                          <td className="screenshots-col-shots">
                            {shots.length > 0 ? (
                              <div className="screenshots-result-thumbs">
                                {shots.map((url) => (
                                  <a
                                    key={url}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => openImageInTab(e, url)}
                                  >
                                    <img src={url} alt="" className="screenshots-thumb" />
                                  </a>
                                ))}
                              </div>
                            ) : (
                              <span className="screenshots-muted">none</span>
                            )}
                          </td>
                          <td className="screenshots-col-testmo">
                            {caseUrl ? (
                              <a href={caseUrl} target="_blank" rel="noopener noreferrer">
                                {item.testCaseId}
                              </a>
                            ) : (
                              item.testCaseId || <span className="screenshots-muted">n/a</span>
                            )}
                          </td>
                          <td className="screenshots-col-result">
                            <span className={`screenshots-pill screenshots-pill--${item.kind}`}>
                              {item.result || 'N/A'}
                            </span>
                          </td>
                          <td className="screenshots-col-details">
                            <button
                              type="button"
                              className="screenshots-button screenshots-button--toggle"
                              onClick={() => togglePanel(rowKey, 'analysis')}
                              disabled={!hasAnalysis}
                              aria-expanded={isOpen(rowKey, 'analysis')}
                            >
                              {isOpen(rowKey, 'analysis') ? '▾' : '▸'} Analysis
                            </button>
                            {item.prompt ? (
                              <a
                                className="screenshots-open-link"
                                href={item.prompt}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Open prompt in a new tab"
                              >
                                Prompt <span aria-hidden="true">↗</span>
                              </a>
                            ) : (
                              <span className="screenshots-open-link is-disabled">
                                Prompt <span aria-hidden="true">↗</span>
                              </span>
                            )}
                          </td>
                        </tr>

                        {hasAnalysis && isOpen(rowKey, 'analysis') && (
                          <tr className="screenshots-panel-row">
                            <td colSpan={4}>
                              <div className="screenshots-panel">
                                {item.analysis && (
                                  <div className="screenshots-result-analysis">
                                    <strong>Analysis:</strong> {item.analysis}
                                  </div>
                                )}
                                {deviations.length > 0 && (
                                  <div className="screenshots-result-deviations">
                                    <strong>Deviations:</strong>
                                    <ul>
                                      {deviations.map((d, idx) => (
                                        <li key={idx}>{d}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    );
                  })}
                </table>
              )}

              {items.length > 0 && visibleItems.length === 0 && (
                <p className="screenshots-empty">No tests match the current filter.</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export { Screenshots };
